import { makeObservable, flow, observable, action, computed } from "mobx";
import { toast } from "sonner";
import { api } from "@/lib/services";
import { ApiRoutes } from "@/lib/routes";

import { Check } from "lucide-react";

import ExportControlsSchema from "@/areas/compliance/schemas/ExportControlsSchema";

import FileIcon from "@/components/shared/FileIcon";
import { LoaderIcon } from "@/components/icons/LoaderIcon";
import { ComplianceConstants } from "./ComplianceConstants";

export default class ExportControlsStore {
  visible = false;
  processing = false;

  constructor(parentStore) {
    makeObservable(this, {
      visible: observable,
      processing: observable,
      show: action,
      hide: action,
      toggle: action,
      save: flow,
      canExport: computed,
    });

    this.parentStore = parentStore;
  }

  get canExport() {
    const authenticationStore = this.parentStore.parentStore.authenticationStore;
    return authenticationStore.isAdmin || authenticationStore.isAgent;
  }

  show() {
    this.visible = true;
  }

  hide() {
    this.visible = false;
  }

  toggle(visible) {
    this.visible = visible === true ? true : visible === false ? false : !this.visible;
  }

  *save(data, options) {
    let toastId = null;

    try {
      this.visible = false;
      this.processing = true;

      const parseResult = ExportControlsSchema.safeParse(data);
      if (!parseResult.success) {
        console.error(parseResult);
        return parseResult;
      }

      toastId = toast(
        <div className="flex w-full items-center gap-2">
          <FileIcon extension={data.format} className="size-8" />
          <div className="flex flex-grow flex-col">
            <span>Exporting controls to {data.format}.</span>
          </div>
          <LoaderIcon variant="primary" size="sm" />
        </div>,
        {
          duration: Infinity,
        }
      );

      const { query, ...rest } = data;
      const storedRegistry = JSON.parse(localStorage.getItem(ComplianceConstants.StateKeys.ControlRegistries)) ?? [];
      const storedRegistryIds = storedRegistry.map((x) => x.id);
      const updatedQuery = { ...query, typeId: storedRegistryIds };

      const response = yield api.post(
        ApiRoutes.forControlExport(),
        { ...rest, query: updatedQuery },
        { responseType: "blob" }
      );

      const contentDisposition = response.headers["content-disposition"];
      const fileNameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
      const matches = fileNameRegex.exec(contentDisposition);
      const fileName = matches && matches[1] ? matches[1].replace(/['"]/g, "") : "controls_export." + data.format;

      const href = URL.createObjectURL(response.data);

      // create "a" HTML element with href to file & click
      const link = document.createElement("a");
      link.href = href;
      link.setAttribute("download", fileName); //or any other extension
      document.body.appendChild(link);
      link.click();

      // clean up "a" element & remove ObjectURL
      document.body.removeChild(link);
      URL.revokeObjectURL(href);

      toast(
        <div className="flex w-full items-center gap-2">
          <FileIcon extension={data.format} className="size-8" />
          <div className="flex flex-grow flex-col">
            <span>Successfully exported controls to {fileName}.</span>
          </div>
          <Check className="h-5 w-5 text-green-600" />
        </div>,
        {
          id: toastId,
          duration: 4000,
        }
      );

      return { success: true, data: response.data, fileName };
    } catch (e) {
      toast.error("Error while exporting controls", {
        description: e.message || "Unexpected error, please try again later.",
        id: toastId,
        duration: 4000,
      });
      return { success: false, error: e };
    } finally {
      this.processing = false;
    }
  }
}
